import React, { useEffect } from "react";
import { ArrowLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { fetchAdminUserMembers } from "../features/admin/slice-extensions/admin-user-members-slice-extension";
import {
  selectAdminUserMembers,
  selectAdminUserMembersStatus,
} from "../features/admin/admin-slice";
import { adminUserMembersOwnerName } from "../features/admin/models/AdminUserMembers";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import { ROUTES } from "../routes";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminMembersList from "../components/admin/AdminMembersList";

/** One instructor account's members — a read-only back-office view. */
const AdminUserMembersPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const detail = useAppSelector(selectAdminUserMembers);
  const status = useAppSelector(selectAdminUserMembersStatus);

  useEffect(() => {
    if (id) {
      dispatch(fetchAdminUserMembers(id));
    }
  }, [dispatch, id]);

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <Link
                to={ROUTES.ADMIN_USERS}
                className="flex items-center gap-1.5 text-sm font-medium text-primary-40 hover:underline dark:text-primary-90"
              >
                <ArrowLeft size={16} />
                All users
              </Link>

              {status === LoadingStatus.Failed && (
                <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <p>Failed to load this account's members.</p>
                </div>
              )}

              {status === LoadingStatus.Loading || !detail ? (
                status !== LoadingStatus.Failed && (
                  <p className="py-10 text-center text-grey-40 dark:text-grey-70">
                    {t("admin.loading")}
                  </p>
                )
              ) : (
                <>
                  <div>
                    <h1 className="text-2xl font-semibold text-grey-10 dark:text-white">
                      {adminUserMembersOwnerName(detail)}
                    </h1>
                    <p className="text-sm text-grey-40 dark:text-grey-70">
                      {detail.email} · {detail.members.length}{" "}
                      {detail.members.length === 1 ? "member" : "members"}
                    </p>
                  </div>
                  <AdminMembersList members={detail.members} />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdminUserMembersPage;
